/**
 * currentness.ts — how CURRENT an answer is, read from the Engine's own result.
 *
 * WHY THIS IS NOT A LOCAL RULE
 * ----------------------------
 * A status is the single most dangerous field in a workbench, because it is the
 * one a reader trusts without checking. "Current" and "stale" look identical on
 * a drawing until somebody acts on the wrong one. So this module does exactly one
 * thing: it reports the terminal status the Engine published, plus the issues and
 * the freshness block that came with it, and it draws NO conclusion of its own.
 *
 * That includes the obvious-looking convenience. There is no `isCurrent` boolean
 * here, and no allowlist of statuses that count as good, because:
 *
 *   • a hand-written list of "the statuses that mean fine" is a second source of
 *     truth about an Engine's state machine, and it is the kind that fails
 *     silently — an Engine that adds an eighth status would be classified by
 *     whatever the list did not mention; and
 *   • the integrated Engine already demonstrates the failure. Its served results
 *     include a terminal status its own published result schema does not
 *     enumerate (`not_found` against an enum of seven). A client that had decided
 *     in advance which statuses are acceptable would have had to guess about the
 *     eighth, and the guess is the bug.
 *
 * So EVERY status is rendered with the Engine's own wording, every unrecognised
 * status is rendered as unrecognised rather than folded into a neighbour, and the
 * only statement this browser makes is the one it can make without authority:
 * "the Engine said this, and this is everything it said with it".
 */

import type { CapabilityDescriptor, JsonSchemaFragment } from "../../api/capabilityManifest";
import { readEnumeration } from "../assessments/determination";

/** How a status is drawn. Ordered by increasing trust, which is also the point. */
export type StatusReading =
  /** The Engine published a value and published a closed vocabulary containing it. */
  | "published"
  /** The Engine returned a value its own schema does not enumerate. */
  | "unrecognised"
  /** The Engine returned no status at all. Distinct from every other case. */
  | "unreported";

export interface IssueReading {
  /** The Engine's own issue code, verbatim. Never normalised. */
  readonly code: string;
  /** The Engine's own severity, verbatim. Never interpreted. */
  readonly severity: string;
  readonly message: string;
}

export interface CurrentnessReading {
  readonly status: StatusReading;
  /** The Engine's status value, verbatim, when it reported one. */
  readonly statusValue: string | undefined;
  /** The Engine's own description of its status vocabulary, when it published one. */
  readonly statusVocabulary: readonly string[];
  readonly issues: readonly IssueReading[];
  /**
   * The Engine's freshness block, verbatim and uninterpreted.
   *
   * Not parsed into "is it stale". The Engine decides what its own freshness
   * fields mean, and a browser that decided would be a second authority over the
   * one question a reviewer most needs to trust.
   */
  readonly freshness: unknown;
  /**
   * The Engine's stated assumptions, verbatim.
   *
   * Surfaced beside the status because a status of "ok" over a stated assumption
   * is a materially different claim from a status of "ok" outright, and only the
   * Engine can say which one it is.
   */
  readonly assumptions: readonly string[];
  /** The Engine's canonical input identity for the answer, when it published one. */
  readonly provenance: unknown;
  /** Whether the Engine reported a status at all. */
  readonly reported: boolean;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function propertiesOf(schema: JsonSchemaFragment | undefined): Record<string, JsonSchemaFragment> {
  const raw = schema?.properties;
  return isRecord(raw) ? (raw as Record<string, JsonSchemaFragment>) : {};
}

/**
 * The property that carries a terminal status, found BY SHAPE.
 *
 * A closed string enumeration in a result schema is a state machine; the first
 * such property is this descriptor's status. Nothing here names `status`, because
 * a field name written in this repository is a claim about the Engine that would
 * be wrong the first time the Engine renamed it — and would be silently wrong
 * rather than loudly.
 */
function statusPropertyOf(descriptor: CapabilityDescriptor): string | undefined {
  return Object.keys(propertiesOf(descriptor.resultSchema)).find(name => {
    const schema = propertiesOf(descriptor.resultSchema)[name];
    return schema?.type === "string" && readEnumeration(schema) !== undefined;
  });
}

/** The string-array property the Engine uses for its stated assumptions, by shape. */
function assumptionsOf(descriptor: CapabilityDescriptor, raw: Record<string, unknown>): readonly string[] {
  const declared = Object.entries(propertiesOf(descriptor.resultSchema))
    .filter(([, schema]) => schema.type === "array" && isRecord(schema.items) && schema.items.type === "string")
    .map(([name]) => name);
  // A result can publish more than one string-array property. Every one of them is
  // reported, because choosing which is "the assumptions" would be a guess; the
  // caller labels them with the Engine's own property names.
  const out: string[] = [];
  for (const name of declared) {
    const value = raw[name];
    if (Array.isArray(value)) for (const entry of value) if (typeof entry === "string") out.push(entry);
  }
  return out;
}

/** The Engine's issues, read from whatever array-of-objects properties it published. */
function issuesOf(descriptor: CapabilityDescriptor, raw: Record<string, unknown>): readonly IssueReading[] {
  const out: IssueReading[] = [];
  for (const schema of Object.values(propertiesOf(descriptor.resultSchema))) {
    if (schema.type !== "array" || !isRecord(schema.items) || schema.items.type !== "object") continue;
    const value = raw[Object.keys(propertiesOf(descriptor.resultSchema)).find(
      name => propertiesOf(descriptor.resultSchema)[name] === schema,
    )!];
    if (!Array.isArray(value)) continue;
    for (const entry of value) {
      if (!isRecord(entry)) continue;
      const code = typeof entry.code === "string" ? entry.code : undefined;
      if (code === undefined) continue;
      out.push({
        code,
        severity: typeof entry.severity === "string" ? entry.severity : "(unreported)",
        message: typeof entry.message === "string" ? entry.message : "",
      });
    }
  }
  return out;
}

/**
 * Read the currentness of one Engine result.
 *
 * `descriptor` is the capability that asked, so every field looked for is one the
 * Engine published a schema for. Nothing is discovered by shape at the payload
 * level, which is what stops this from inventing a status in a result that has
 * none.
 */
export function readCurrentness(descriptor: CapabilityDescriptor, raw: unknown): CurrentnessReading {
  const record = isRecord(raw) ? raw : {};
  const statusField = statusPropertyOf(descriptor);
  const value = statusField !== undefined ? record[statusField] : undefined;
  const vocabulary = statusField !== undefined
    ? (readEnumeration(propertiesOf(descriptor.resultSchema)[statusField])?.values
      ?? []).filter((entry): entry is string => typeof entry === "string")
    : [];

  let status: StatusReading;
  if (value === undefined || value === null || value === "") {
    status = "unreported";
  } else if (typeof value !== "string") {
    // A non-string status is not a status this browser can read. It is reported
    // as unreported rather than coerced, because coercing is how a value becomes
    // a claim.
    status = "unrecognised";
  } else if (vocabulary.length > 0 && !vocabulary.includes(value)) {
    status = "unrecognised";
  } else {
    status = "published";
  }

  return {
    status,
    statusValue: typeof value === "string" ? value : undefined,
    statusVocabulary: vocabulary,
    issues: issuesOf(descriptor, record),
    freshness: record.freshness,
    assumptions: assumptionsOf(descriptor, record),
    provenance: record.provenance,
    reported: value !== undefined,
  };
}

/**
 * The one sentence this browser is entitled to make about a status.
 *
 * It is a statement about the PUBLICATION, not about the engineering. Every branch
 * declines to conclude, and the two failure branches say specifically what is
 * missing, because "unrecognised" without a reason is indistinguishable from a
 * bug in the reader.
 */
export function currentnessStatement(reading: CurrentnessReading): { text: string; settled: boolean } {
  if (reading.status === "unreported") {
    return {
      text: reading.statusValue === undefined && reading.statusVocabulary.length === 0
        ? "This result's schema publishes no status vocabulary, so this browser makes no claim about how current it is."
        : "The Engine reported no status for this result. That is not a claim of currency.",
      settled: false,
    };
  }
  if (reading.status === "unrecognised") {
    return {
      text: `The Engine reported "${reading.statusValue}", which is not in the vocabulary its own schema publishes `
        + `(${reading.statusVocabulary.join(", ") || "none"}). This browser draws no conclusion from a value it `
        + "does not understand, and does not treat it as the nearest published status.",
      settled: false,
    };
  }
  return {
    text: `The Engine's own status is "${reading.statusValue}". This browser repeats it and does not interpret it: `
      + "the Engine decides what its statuses mean, and a client that reclassified them would be a second authority "
      + "over the one question a reviewer is most likely to trust.",
    settled: true,
  };
}
